// src/pages/Files.jsx - Updated to integrate with Upload Queue

import React, { useState, useMemo, useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { useFileContext } from "../contexts/FileContext";
import { useUploadQueue } from "../contexts/UploadQueueContext";
import useFileOperations from "../hooks/useFileOperations";
import { useFolderNavigation } from "../hooks/useFolderNavigation";

// Component imports
import Sidebar from "../components/dashboard/Sidebar";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import FileExplorer from "../components/files/FileExplorer";
import FileSearchBar from "../components/files/FileSearchBar";
import FolderPath from "../components/files/FolderPath";
import FileSortControls from "../components/files/FileSortControls";
import UploadQueueModal from "../components/upload/UploadQueueModal";

// Modal imports
import CreateFolderModal from "../components/modals/FileModals/CreateFolderModal";
import FileUploadModal from "../components/modals/FileModals/FileUploadModal";
import FileViewerModal from "../components/modals/FileModals/FileViewerModal";
import DeleteConfirmationModal from "../components/modals/FileModals/DeleteConfirmationModal";
import ProgressModal from "../components/modals/FileModals/ProgressModal";
import RenameModal from "../components/modals/FileModals/RenameModal";

import { Alert } from "react-bootstrap";
import { checkPaymentStatus } from "../services/api.service";

import { getUserCredentials } from "../services/api.service";
import { abortMultipartUpload } from "../services/file.service";
import { usePaymentStatus } from "../hooks/usePaymentStatus";

// Styles
import "../styles/pages/files.css";
import "../styles/components/ListHeader.css";

const Files = () => {
  // Context and hooks
  const {
    isLoading,
    currentFolder,
    currentPath,
    viewMode,
    setViewMode,
    selectedItems,
    clearSelection,
    searchQuery,
    setSearchQuery,
    sortType,
    sortMethod,
    sortOrder,
    setSortType,
    setSortMethod,
    setSortOrder,
    isUploadInProgress,
    uploadProgress,
    uploadMessage,
    setUploadProgress,
    resetUploadState,
    toggleItemSelection,
  } = useFileContext();

  const {
    uploadFiles,
    downloadFile,
    downloadFolder,
    deleteFile,
    deleteFolder,
    viewFile,
    createFolder,
    renameFile,
    cancelOperation,
    activeOperations,
    error,
    clearError,
    fileToView,
    setFileToView,
    downloadProgress,
    downloadMessage,
    showDownloadProgress,
    handleDownloadProgressClose,
  } = useFileOperations();

  const {
    navigateToFolder,
    navigateUp,
    refreshFolder,
    getBucketContent,
    downloadProgress: navDownloadProgress,
    downloadMessage: navDownloadMessage,
    showDownloadProgress: showNavDownloadProgress,
    handleDownloadProgressClose: handleNavDownloadProgressClose,
  } = useFolderNavigation();

  const [inheritanceStatus, setInheritanceStatus] = useState({
    loading: false,
    success: false,
    error: null,
    fileId: null,
  });

  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  const [isAddingToInheritance, setIsAddingToInheritance] = useState(false);
  const { addToQueue, showQueue, onUploadComplete } = useUploadQueue();
  const [isDeletingItems, setIsDeletingItems] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentFile: "",
  });
  const { isAccountFrozen, showFrozenAlert } = usePaymentStatus();
  const [deleteAbortController, setDeleteAbortController] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");

  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });

    // Auto dismiss after 5 seconds
    setTimeout(() => {
      setAlert((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  useEffect(() => {
    const handleOnline = async () => {

      // Small delay to ensure connection is stable
      setTimeout(async () => {
        // await cleanupAbandonedUploads();
      }, 2000);
    };

    const handleOffline = () => {
      // Optionally show a warning about active uploads
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes

    const intervalId = setInterval(async () => {
      // Only run if document is visible and online
      if (!document.hidden && navigator.onLine) {
        const hasAbandoned = checkForAbandonedUploads(); // Quick check first
        if (hasAbandoned) {
          // await cleanupAbandonedUploads();
        }
      }
    }, CLEANUP_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && navigator.onLine) {
        // User returned to tab and is online

        // Small delay to avoid too frequent calls
        setTimeout(async () => {
          const hasAbandoned = checkForAbandonedUploads();
          if (hasAbandoned) {
            // await cleanupAbandonedUploads();
          }
        }, 1000);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const handleUploadComplete = async () => {

      // await cleanupAbandonedUploads();

      setTimeout(() => {

        // ✅ DIRECT REFRESH - always uses current state
        // Check current path length at execution time, not closure time
        const isAtRoot = !currentPath || currentPath.length === 0;

        if (isAtRoot) {
          getBucketContent(true);
        } else {
          refreshFolder(true);
        }
      }, 1000);
    };

    const unregister = onUploadComplete(handleUploadComplete);
    return unregister;
  }, [onUploadComplete, currentPath, getBucketContent, refreshFolder]);

  // Component state
  const [sidebarToggled, setSidebarToggled] = useState(false);
  const [modals, setModals] = useState({
    upload: false,
    createFolder: false,
    deleteItem: false,
    viewFile: false,
    renameFile: false,
  });

  const [itemToDelete, setItemToDelete] = useState(null);
  const [fileForInheritance, setFileForInheritance] = useState(null);
  const [fileToRename, setFileToRename] = useState(null);
  const [isCleaningUploads, setIsCleaningUploads] = useState(false);

  // Load initial data
  useEffect(() => {
    getBucketContent();
  }, []);

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      // Select all filtered items
      getFilteredAndSortedFiles.forEach((item, index) => {
        const enrichedItem = {
          ...item,
          path:
            item.path ||
            (currentPath.length > 0
              ? `${currentPath.join("/")}/${item.name}`
              : item.name),
          arrayIndex: index,
        };

        // Only add if not already selected
        if (
          !selectedItems.some(
            (selectedItem) =>
              selectedItem.arrayIndex === index ||
              (selectedItem.name === item.name &&
                selectedItem.type === item.type)
          )
        ) {
          toggleItemSelection(enrichedItem);
        }
      });
    } else {
      // Deselect all
      clearSelection();
    }
  };

  // Check if all items are selected
  const areAllItemsSelected = () => {
    if (getFilteredAndSortedFiles.length === 0) return false;
    return getFilteredAndSortedFiles.every((item, index) =>
      selectedItems.some(
        (selectedItem) =>
          selectedItem.arrayIndex === index ||
          (selectedItem.name === item.name && selectedItem.type === item.type)
      )
    );
  };

  // Check if some (but not all) items are selected
  const areSomeItemsSelected = () => {
    return selectedItems.length > 0 && !areAllItemsSelected();
  };

  const handleRenameFile = async (fileItem, newName) => {
    try {
      const success = await renameFile(fileItem, newName);

      if (success) {
        showAlert(`File renamed successfully to "${newName}"`, "success");
        closeModal("renameFile");
        setFileToRename(null);

        // Refresh the current directory
        setTimeout(() => {
          const currentPathString =
            currentPath.length > 0 ? currentPath.join("/") : "";
          if (currentPathString) {
            refreshFolder(true);
          } else {
            getBucketContent(true);
          }
        }, 500);

        return true;
      }
      return false;
    } catch (error) {
      showAlert(`Failed to rename file: ${error.message}`, "danger");
      return false;
    }
  };

  const handleFileUpload = (files, uploadType = "files") => {
    if (isAccountFrozen) {
      showFrozenAlert(showAlert);
      closeModal("upload");
      return false;
    }
    const currentFolderPath =
      currentPath.length > 0 ? currentPath.join("/") : "";
    try {
      const queueIds = addToQueue(files, currentFolderPath, uploadType);

      closeModal("upload");
      showQueue();

      return true;
    } catch (error) {
      console.error("Error adding files to upload queue:", error);
      return false;
    }
  };

  const checkForAbandonedUploads = () => {
    const CLEANUP_TIMEOUT = 10 * 60 * 1000; // 10 minutes

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("upload_")) {
        try {
          const metadata = JSON.parse(localStorage.getItem(key));
          const age = Date.now() - metadata.timestamp;

          if (age > CLEANUP_TIMEOUT || metadata.needsCleanup) {
            return true;
          }
        } catch (error) {
          return true; // Invalid entries should be cleaned
        }
      }
    }
    return false;
  };

  // Full cleanup function (with API calls)
  const cleanupAbandonedUploads = async () => {
    if (isCleaningUploads) {
      return;
    }

    const CLEANUP_TIMEOUT = 10 * 60 * 1000; // 10 minutes
    let foundAbandonedUploads = false;

    setIsCleaningUploads(true);

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        if (key?.startsWith("upload_")) {

          try {
            const rawData = localStorage.getItem(key);
            const metadata = JSON.parse(rawData);

            const age = Date.now() - metadata.timestamp;

            if (age > CLEANUP_TIMEOUT || metadata.needsCleanup) {
              foundAbandonedUploads = true;
              
              if (metadata.type === "multipart" && metadata.uploadId) {
                                try {
                  const credentials = getUserCredentials();
                  const token =
                    credentials?.token || sessionStorage.getItem("userToken");

                  if (token) {
                    await abortMultipartUpload(
                      metadata.uploadId,
                      metadata.key,
                      metadata.bucket,
                      token
                    );
                    
                  } else {
                    console.error("❌ No token available for cleanup");
                  }
                } catch (cleanupError) {
                  console.error(
                    `💥 Cleanup error for ${metadata.uploadId}:`,
                    cleanupError
                  );
                  console.error(`💥 Error details:`, cleanupError.message);
                }
              }

              localStorage.removeItem(key);
            }
          } catch (parseError) {
            console.error(
              `❌ Error parsing upload metadata ${key}:`,
              parseError
            );
            localStorage.removeItem(key);
          }
        }
      }

      if (foundAbandonedUploads) {
        showAlert("Cleaned up incomplete uploads", "info");
      }
    } catch (globalError) {
      console.error(`💥 Global cleanup error:`, globalError);
    } finally {
      setIsCleaningUploads(false);
    }
  };

  const handleFolderCreatedFromModal = () => {


    setTimeout(() => {
      forceFullRefresh(); // Call the refresh function directly
    }, 500);
  };

  const getFilteredAndSortedFiles = useMemo(() => {

    if (!currentFolder || !currentFolder.children) {
      return [];
    }

    // Get the files from the children array
    let items = [...currentFolder.children];

    if (items.length === 0) {
      return [];
    }

    // 1. Apply Type Filter
    if (sortType && sortType !== "all") {
      items = items.filter((item) => {
        // Handle folders filter
        if (sortType === "folders") {
          return item.type === "folder";
        }

        // For files, check extensions
        if (item.type === "file" || item.type !== "folder") {
          const fileName = String(item.name || "");
          const extension = fileName.split(".").pop().toLowerCase();

          if (
            sortType === "images" &&
            ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(
              extension
            )
          ) {
            return true;
          }
          if (
            sortType === "documents" &&
            [
              "pdf",
              "doc",
              "docx",
              "txt",
              "rtf",
              "xls",
              "xlsx",
              "ppt",
              "pptx",
            ].includes(extension)
          ) {
            return true;
          }
          if (
            sortType === "videos" &&
            ["mp4", "avi", "mov", "wmv", "mkv", "webm"].includes(extension)
          ) {
            return true;
          }
          return false;
        }
        return false;
      });
    }

    // 2. Apply Sorting
    if (sortMethod) {
      items.sort((a, b) => {
        // Always put folders first
        if (a.type === "folder" && b.type !== "folder") return -1;
        if (a.type !== "folder" && b.type === "folder") return 1;

        // Sort by chosen method
        switch (sortMethod) {
          case "name":
            const nameA = String(a.name || "").toLowerCase();
            const nameB = String(b.name || "").toLowerCase();
            return sortOrder === "asc"
              ? nameA.localeCompare(nameB)
              : nameB.localeCompare(nameA);

          case "size":
            const sizeA = a.type === "folder" ? 0 : Number(a.size || 0);
            const sizeB = b.type === "folder" ? 0 : Number(b.size || 0);
            return sortOrder === "asc" ? sizeA - sizeB : sizeB - sizeA;

          case "date":
            const dateA = new Date(
              a.lastModified || a.lastEdited || 0
            ).getTime();
            const dateB = new Date(
              b.lastModified || b.lastEdited || 0
            ).getTime();
            return sortOrder === "asc" ? dateA - dateB : dateB - dateA;

          default:
            return 0;
        }
      });
    }

    return items;
  }, [currentFolder, sortType, sortMethod, sortOrder]);

  // Handle folder creation
  const handleCreateFolder = (folderName) => {
    if (isAccountFrozen) {
      showFrozenAlert(showAlert);
      closeModal("createFolder");
      return;
    }
    const parentPath = currentPath.length > 0 ? currentPath.join("/") : "";
    createFolder(folderName, parentPath).then((success) => {
      if (success) {
        closeModal("createFolder");
        refreshFolder();
      }
    });
  };

  // Handle item deletion
  // In Files.jsx, update handleDeleteConfirm function:
  const handleDeleteConfirm = () => {
    if (!itemToDelete) return;


    // Handle multiple item deletion
    if (itemToDelete.type === "multiple" && itemToDelete.items) {

      // Process each item for deletion
      const deletePromises = itemToDelete.items.map((item) => {

        if (item.type === "folder") {
          return deleteFolder(item);
        } else {
          return deleteFile(item);
        }
      });

      // Wait for all deletions to complete
      Promise.allSettled(deletePromises)
        .then((results) => {
          const successful = results.filter(
            (result) => result.status === "fulfilled"
          ).length;
          const failed = results.filter(
            (result) => result.status === "rejected"
          ).length;

          if (successful > 0) {
            // Clear selection after successful deletions
            clearSelection();
            closeModal("deleteItem");
            setItemToDelete(null);
            refreshFolder();
          }

          if (failed > 0) {
          }
        })
        .catch((error) => {
          console.error("Error during bulk deletion:", error);
        });

      return;
    }

    // Handle single item deletion (existing logic)
    let itemWithPath = itemToDelete;

    if (itemToDelete.type !== "folder" && !itemToDelete.path) {
      const folderPathString =
        currentPath.length > 0 ? currentPath.join("/") : "";

      if (folderPathString) {
        itemWithPath = {
          ...itemToDelete,
          path: `${folderPathString}/${itemToDelete.name}`,
        };
      } else {
        itemWithPath = {
          ...itemToDelete,
          path: itemToDelete.name,
        };
      }
    }

    let subItem = itemWithPath;
    subItem.name = itemWithPath.path;
    const deletePromise =
      itemWithPath.type === "folder"
        ? deleteFolder(subItem)
        : deleteFile(itemWithPath);

    deletePromise
      .then((success) => {
        if (success) {
          closeModal("deleteItem");
          setItemToDelete(null);
          refreshFolder();
        }
      })
      .catch((error) => {
        console.error("Error during deletion:", error);
      });
  };

  // Handle file viewing
  const handleViewFile = (fileItem) => {
    // Construct the full path for the file
    const fullPath =
      currentPath.length > 0
        ? `${currentPath.join("/")}/${fileItem.name}`
        : fileItem.name;

    // Create a new fileItem with the full path
    const fileItemWithPath = {
      ...fileItem,
      path: fullPath,
    };

    viewFile(fileItemWithPath).then((result) => {
      if (result) {
        openModal("viewFile");
      }
    });
  };

  // Handle item download
  const handleDownload = (item) => {
    if (item.type === "folder") {
      downloadFolder(item);
    } else {
      downloadFile(item);
    }
  };

  // Handle multiple items download
  const handleDownloadSelected = () => {
    selectedItems.forEach((item) => {
      handleDownload(item);
    });
  };

  // Handle multiple items deletion
  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      showAlert("No items selected for deletion", "warning");
      return;
    }

    // Warn user if deleting many files
    const isLargeDelete = selectedItems.length > 10;
    const confirmMessage = isLargeDelete
      ? `Are you sure you want to delete ${selectedItems.length} selected item(s)?\n\nThis may take a while and cannot be undone.`
      : `Are you sure you want to delete ${selectedItems.length} selected item(s)?\n\nThis action cannot be undone.`;

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) {
      return;
    }

    // Create AbortController for cancellation
    const abortController = new AbortController();
    setDeleteAbortController(abortController);
    let deleteAborted = false;

    try {
      // Import the bulk delete function
      const { deleteSelectedFiles } = await import("../services/file.service");

      // Show progress modal
      setShowProgressModal(true);
      setProgressPercentage(0);

      // Set initial message based on file count
      const initialMessage = isLargeDelete
        ? `Deleting ${selectedItems.length} items... This may take a while.`
        : `Deleting ${selectedItems.length} selected items...`;
      setProgressMessage(initialMessage);

      // Progress callback to update the modal
      const progressCallback = (current, total, message) => {

        // Check for cancellation before updating
        checkCancellation();
        if (deleteAborted) return;

        // Calculate simple progress: 0% at start, 100% when complete
        let percentage = 0;
        if (
          message.includes("completed") ||
          message.includes("Deletion completed")
        ) {
          percentage = 100;
        } else if (
          message.includes("Starting") ||
          message.includes("Deleting")
        ) {
          percentage = 25; // Show some progress to indicate it started
        }

        setProgressPercentage(percentage);
        setProgressMessage(message);
      };

      // Monitor for cancellation
      const checkCancellation = () => {
        if (abortController.signal.aborted) {
          deleteAborted = true;
        }
      };

      // Perform bulk delete with abort signal and progress callback
      const result = await deleteSelectedFiles(
        selectedItems,
        "files", // or your bucket name
        progressCallback,
        abortController.signal
      );

      // Check if operation was aborted
      if (deleteAborted || abortController.signal.aborted) {
        setProgressMessage("Deletion cancelled by user");
        setProgressPercentage(0);
        setTimeout(() => {
          setShowProgressModal(false);
          setDeleteAbortController(null);
          showAlert("Deletion cancelled by user", "warning");
        }, 1500);
        return;
      }

      // Show completion
      setProgressPercentage(100);
      setProgressMessage(
        `Successfully processed ${selectedItems.length} items`
      );

      // Show results to user (after modal auto-closes)
      setTimeout(() => {
        if (result.successCount === selectedItems.length) {
          showAlert(
            `Successfully deleted all ${result.successCount} items`,
            "success"
          );
        } else if (result.successCount > 0) {
          showAlert(
            `Partially completed: ${result.successCount} deleted, ${result.failureCount} failed`,
            "warning"
          );
        } else {
          showAlert("Failed to delete any items", "danger");
        }
      }, 2000); // Wait for modal to auto-close

      // Clear selection and refresh
      clearSelection();
      refreshFolder(true);
    } catch (error) {
      if (
        error.name === "AbortError" ||
        deleteAborted ||
        abortController.signal.aborted
      ) {
        setProgressMessage("Deletion was cancelled");
        setProgressPercentage(0);
        setTimeout(() => {
          setShowProgressModal(false);
          setDeleteAbortController(null);
          showAlert("Deletion was cancelled", "warning");
        }, 1500);
      } else {
        console.error("❌ Bulk delete error:", error);
        setProgressMessage(`Error: ${error.message}`);
        setProgressPercentage(0);
        setTimeout(() => {
          setShowProgressModal(false);
          setDeleteAbortController(null);
          showAlert(`Failed to delete items: ${error.message}`, "danger");
        }, 1500);
      }
    }
  };

  // Add this separate function to handle modal close/cancel (outside handleDeleteSelected)
  const handleModalClose = () => {
    if (
      progressPercentage < 100 &&
      deleteAbortController &&
      !deleteAbortController.signal.aborted
    ) {
      // User clicked cancel during operation
      deleteAbortController.abort();
      setProgressMessage("Cancelling deletion...");
    } else {
      // Operation complete or already cancelled, just close modal
      setShowProgressModal(false);
      setDeleteAbortController(null);
    }
  };

  // Handle add to inheritance
  const handleAddToInheritance = async (fileItem) => {
    const fileId = `${fileItem.name}-${fileItem.path || ""}`;
    setInheritanceStatus({
      loading: true,
      success: false,
      error: null,
      fileId,
    });

    try {
      // Check payment status first
      const paymentStatus = await checkPaymentStatus();
      if (paymentStatus.isOverdue) {
        setInheritanceStatus({
          loading: false,
          success: false,
          error:
            "Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.",
          fileId,
        });
        // Auto-reset after showing error
        setTimeout(() => {
          setInheritanceStatus({
            loading: false,
            success: false,
            error: null,
            fileId: null,
          });
        }, 5000);
        return;
      }

      // Import the function from file service
      const { addFileToCurrentInheritance, addFolderToCurrentInheritance  } = await import(
        "../services/file.service"
      );

      // Construct the full file path
      const itemPath =
  fileItem.path ||
  (currentPath.length > 0
    ? `${currentPath.join("/")}/${fileItem.name}`
    : fileItem.name);

      if (fileItem.type === 'folder') {
  await addFolderToCurrentInheritance(itemPath);
} else {
  await addFileToCurrentInheritance(itemPath);
}

      setInheritanceStatus({
        loading: false,
        success: true,
        error: null,
        fileId,
      });
      showAlert(`${fileItem.type === 'folder' ? 'Folder' : 'File'} successfully added to endowment!`, "success");

      window.scrollTo({ top: 0, behavior: "smooth" });

      // Auto-reset success state after 3 seconds
      setTimeout(() => {
        setInheritanceStatus({
          loading: false,
          success: false,
          error: null,
          fileId: null,
        });
      }, 3000);
    } catch (error) {
      const errorMessage = `Error adding file to endowment: ${error.message}`;
      setInheritanceStatus({
        loading: false,
        success: false,
        error: errorMessage,
        fileId,
      });
      showAlert(errorMessage, "danger");

      window.scrollTo({ top: 0, behavior: "smooth" });

      // Auto-reset error state after 5 seconds
      setTimeout(() => {
        setInheritanceStatus({
          loading: false,
          success: false,
          error: null,
          fileId: null,
        });
      }, 5000);
    }
  };

  // Modal helpers
  const openModal = (modalName) => {
    if (isAccountFrozen) {
      showFrozenAlert(showAlert);
      closeModal("createFolder");
      return;
    }
    setModals((prev) => ({ ...prev, [modalName]: true }));
  };

  const closeModal = (modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: false }));
  };

  const forceFullRefresh = async () => {
    try {
      // await cleanupAbandonedUploads();

      // Clear cache entries
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("cache-")) {
          localStorage.removeItem(key);
        }
      });

      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith("cache-")) {
          sessionStorage.removeItem(key);
        }
      });

      const currentPathString =
        currentPath.length > 0 ? currentPath.join("/") : "";


      if (currentPathString) {
        // We're in a subfolder
        await refreshFolder(true);
      } else {
        // We're at root
        await getBucketContent(true);
      }
    } catch (error) {
      console.error("Error during refresh:", error);
    }
  };

  return (
    <div id="page-top">
      <div id="wrapper" className="d-flex">
        <Sidebar toggled={sidebarToggled} setToggled={setSidebarToggled} />

        <div
          id="content-wrapper"
          className={`d-flex flex-column ${
            sidebarToggled ? "content-toggled" : ""
          }`}
        >
          <div id="content">
            <DashboardHeader
              toggleSidebar={() => setSidebarToggled(!sidebarToggled)}
              title="My Files"
            />

            <div className="container-fluid">
              {alert.show && (
                <Alert
                  variant={alert.variant}
                  onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
                  dismissible
                  className="mt-3"
                >
                  {alert.message}
                </Alert>
              )}
              {error && (
                <div
                  className="alert alert-danger alert-dismissible fade show"
                  role="alert"
                >
                  {error}
                  <button type="button" className="close" onClick={clearError}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )}

              <div className="row">
                <div className="col-lg-12">
                  <div
                    className="card shadow mb-4"
                    style={{
                      height: "auto",
                      display: "flex",
                      flexDirection: "column",
                      padding: "5px",
                    }}
                  >
                    {/* Folder navigation */}
                    <FolderPath
                      currentPath={currentPath}
                      navigateToFolder={navigateToFolder}
                      navigateUp={navigateUp}
                    />

                    {/* File controls */}
                    <div className="file-controls-container p-2">
                      {/* Mobile-friendly container with flex column on small screens */}
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                        {/* Search bar - full width on mobile */}
                        <div className="search-wrapper mb-2 mb-md-0">
                          <FileSearchBar
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                          />
                        </div>

                        {/* Controls group - horizontally aligned even on mobile */}
                        <div className="d-flex file-controls-right flex-wrap gap-1">
                          {/* Refresh button */}
                          <button
                            className="btn btn-outline-secondary me-1"
                            onClick={forceFullRefresh}
                            disabled={isLoading}
                          >
                            <i
                              className={`fas fa-sync-alt ${
                                isLoading ? "fa-spin" : ""
                              }`}
                            ></i>
                          </button>

                          {/* View mode buttons */}
                          <div className="btn-group me-1">
                            <button
                              className={`btn btn-outline-secondary ${
                                viewMode === "list" ? "active" : ""
                              }`}
                              onClick={() => setViewMode("list")}
                              title="List view"
                            >
                              <i className="fas fa-list"></i>
                            </button>
                            <button
                              className={`btn btn-outline-secondary ${
                                viewMode === "grid" ? "active" : ""
                              }`}
                              onClick={() => setViewMode("grid")}
                              title="Grid view"
                            >
                              <i className="fas fa-th"></i>
                            </button>
                          </div>

                          {/* Sort controls */}
                          <FileSortControls
                            sortType={sortType}
                            sortMethod={sortMethod}
                            sortOrder={sortOrder}
                            setSortType={setSortType}
                            setSortMethod={setSortMethod}
                            setSortOrder={setSortOrder}
                          />
                        </div>
                      </div>
                      {getFilteredAndSortedFiles.length > 0 && (
                        <div className="select-all-container mt-2 pt-2 border-top">
                          <div className="d-flex align-items-center">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="selectAllCheckbox"
                                checked={areAllItemsSelected()}
                                ref={(input) => {
                                  if (input)
                                    input.indeterminate =
                                      areSomeItemsSelected();
                                }}
                                onChange={(e) =>
                                  handleSelectAll(e.target.checked)
                                }
                                style={{ width: "18px", height: "18px" }}
                              />
                              <label
                                className="form-check-label ms-2"
                                htmlFor="selectAllCheckbox"
                              >
                                {selectedItems.length > 0
                                  ? `${selectedItems.length} of ${getFilteredAndSortedFiles.length} selected`
                                  : `Select all ${getFilteredAndSortedFiles.length} items`}
                              </label>
                            </div>

                            
                          </div>
                        </div>
                      )}
                    </div>

                    {/* File explorer */}
                    <div
                      className="card-body header-sort p-0"
                      style={{
                        flexGrow: 1,
                        overflowY: "auto",
                        overflowX: "visible",
                        padding: "5px",
                      }}
                    >
                      <FileExplorer
                        isLoading={isLoading}
                        currentFolder={{
                          ...currentFolder,
                          children: getFilteredAndSortedFiles,
                        }}
                        searchQuery={searchQuery}
                        viewMode={viewMode}
                        onFileClick={handleViewFile}
                        onFolderClick={navigateToFolder}
                        onDeleteClick={(item) => {
                          setItemToDelete(item);
                          openModal("deleteItem");
                        }}
                        onDownloadClick={handleDownload}
                        onAddToInheritanceClick={handleAddToInheritance}
                        onRenameClick={(item) => {
                          setFileToRename(item);
                          openModal("renameFile");
                        }}
                        onCreateFolderClick={() => openModal("createFolder")}
                        onUploadFilesClick={() => openModal("upload")}
                        toggleItemSelection={toggleItemSelection}
                        inheritanceStatus={inheritanceStatus}
                      />
                    </div>
                  </div>

                  {/* Floating action buttons */}
                  <div className="floating-button-container">
                    {selectedItems.length > 0 ? (
                      <>
                        <button
                          className="btn btn-danger floating-button mr-2"
                          onClick={handleDeleteSelected}
                          disabled={isDeletingItems}
                          title="Delete selected"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                        <button
                          className="btn btn-info floating-button"
                          onClick={handleDownloadSelected}
                          disabled={isDeletingItems}
                          title="Download selected"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-icon-add btn"
                        onClick={() => openModal("upload")}
                        disabled={isDeletingItems}
                        title="Upload files"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Existing Modals */}
      <FileUploadModal
        show={modals.upload}
        onHide={() => closeModal("upload")}
        onUpload={handleFileUpload}
        currentPath={currentPath}
        onFolderCreated={handleFolderCreatedFromModal}
      />

      <CreateFolderModal
        show={modals.createFolder}
        onHide={() => closeModal("createFolder")}
        onCreate={handleCreateFolder}
        currentPath={currentPath}
      />

      <DeleteConfirmationModal
        show={modals.deleteItem}
        onHide={() => {
          closeModal("deleteItem");
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        item={itemToDelete}
      />

      <RenameModal
        show={modals.renameFile}
        onHide={() => {
          closeModal("renameFile");
          setFileToRename(null);
        }}
        onRename={handleRenameFile}
        fileItem={fileToRename}
      />

      <FileViewerModal
        show={modals.viewFile}
        onHide={() => {
          closeModal("viewFile");
          setFileToView(null);
          setInheritanceStatus({ loading: false, success: false, error: null });
        }}
        fileData={fileToView}
        onDownload={fileToView ? () => downloadFile(fileToView) : null}
        onDelete={
          fileToView
            ? () => {
                closeModal("viewFile");
                setItemToDelete(fileToView);
                openModal("deleteItem");
              }
            : null
        }
        onAddToInheritance={
          fileToView
            ? () => {
                handleAddToInheritance(fileToView);
              }
            : null
        }
        inheritanceStatus={inheritanceStatus}
      />

      <ProgressModal
        show={
          isUploadInProgress || 
          showDownloadProgress || 
          showNavDownloadProgress || 
          showProgressModal || 
          Object.values(activeOperations).some(op => op)
        }
        progress={
          isUploadInProgress ? uploadProgress :
          showDownloadProgress ? downloadProgress :
          showNavDownloadProgress ? navDownloadProgress :
          showProgressModal ? progressPercentage :
          50
        }
        message={
          isUploadInProgress ? uploadMessage :
          showDownloadProgress ? downloadMessage :
          showNavDownloadProgress ? navDownloadMessage :
          showProgressModal ? progressMessage : 
          'Operation in progress...'
        }
        onCancel={
          isUploadInProgress ? cancelOperation :
          showDownloadProgress ? handleDownloadProgressClose :
          showNavDownloadProgress ? handleNavDownloadProgressClose :
          showProgressModal ? handleModalClose : 
          cancelOperation
        }
      />

    </div>
  );
};

export default Files;
